import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Seo from "@/components/Seo";
import ArtBackdrop from "@/components/ArtBackdrop";
import { apiClient } from "@/lib/api";
import {
  ShieldCheck,
  Sparkles,
  Building2,
  TrendingUp,
  ArrowRight,
  Check,
} from "lucide-react";

const FALLBACK_PRICING = {
  monthly_subscription: { label: "Abonnement mensuel spécialiste", amount_eur: 79, vat: "HT", description: "Accès complet à la plateforme et aux dossiers reçus." },
  lead_fee: { label: "Frais de mise en relation", amount_eur: 29, vat: "HT", description: "Facturé par dossier qualifié transmis." },
  tech_commission_pct: { label: "Commission technologique", amount_pct: 10, description: "Commission sur les consultations payées via CONFILEX, sous réserve d'autorisation déontologique." },
  premium_visibility: { label: "Visibilité premium", amount_eur: 149, vat: "HT", description: "Mise en avant priorisée et fiche éditoriale enrichie." },
};

export default function Rejoindre() {
  const [pricing, setPricing] = useState(FALLBACK_PRICING);

  useEffect(() => {
    apiClient
      .get("/external-experts/inexistant/affiliation-pricing")
      .catch(() => null);
    // Endpoint public pour récupérer les tarifs courants
    apiClient
      .get("/consultation-pricing") // route publique existante — on récupère aussi via admin si besoin
      .catch(() => null);
  }, []);

  const pillars = [
    { icon: ShieldCheck, title: "Conformité & déontologie", text: "Architecture compatible avec les règles de votre ordre professionnel. Commissions appliquées uniquement si juridiquement autorisé." },
    { icon: Building2, title: "Espace spécialiste sécurisé", text: "Dossiers chiffrés, messagerie cloisonnée, signature électronique, gestion des rendez-vous, prise de paiement intégrée." },
    { icon: Sparkles, title: "Dossiers pré-qualifiés par IA", text: "Chaque demande est analysée par Claude IA — vous recevez une synthèse structurée, pas un message brut." },
    { icon: TrendingUp, title: "Visibilité éditoriale", text: "Publiez articles d'analyse, conférences, podcasts — référencement SEO institutionnel CONFILEX." },
  ];

  return (
    <div className="min-h-screen bg-[#FAF7F1] text-[#0B3A2A]">
      <Seo
        title="Rejoindre CONFILEX · Espace professionnels"
        description="Professionnels du droit, du chiffre, de l'immobilier : rejoignez le réseau institutionnel CONFILEX. Dossiers pré-qualifiés par IA, paiement sécurisé, visibilité éditoriale."
      />
      <Navbar variant="light" />

      {/* HERO */}
      <section className="relative pt-28 pb-20 overflow-hidden">
        <ArtBackdrop
          src="/art/sorola/ete-2.jpg"
          opacity={0.18}
          blur={28}
          saturate={0.7}
          overlay="linear-gradient(180deg, rgba(250,247,241,0.82) 0%, rgba(250,247,241,0.95) 80%)"
        />
        <div className="relative max-w-5xl mx-auto px-6">
          <p className="text-[11px] uppercase tracking-[0.3em] text-[#C9A961] mb-5">
            Réseau institutionnel CONFILEX
          </p>
          <h1 className="font-serif italic text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight mb-6">
            Rejoignez un réseau<br />de professionnels exigeants.
          </h1>
          <p className="text-base sm:text-lg text-slate-700 leading-relaxed max-w-2xl">
            CONFILEX est une infrastructure premium d'orientation et de structuration documentaire.
            Nous référençons des professionnels reconnus pour leur sérieux, leur méthode et leur sens du conseil.
          </p>
          <div className="mt-8 flex gap-3 flex-wrap">
            <Link
              to="/expert/apply"
              data-testid="cta-apply"
              className="inline-flex items-center gap-2 bg-[#0B3A2A] text-white px-6 py-3 rounded-lg hover:brightness-110 transition"
            >
              Candidater au référencement <ArrowRight className="w-4 h-4" />
            </Link>
            <a
              href="#offres"
              className="inline-flex items-center gap-2 border border-[#0B3A2A]/20 text-[#0B3A2A] px-6 py-3 rounded-lg hover:bg-white transition"
            >
              Voir les conditions
            </a>
          </div>
        </div>
      </section>

      {/* PILIERS */}
      <section className="py-16 bg-white border-y border-[#0B3A2A]/8">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-14">
            <p className="text-[11px] uppercase tracking-[0.28em] text-[#C9A961] mb-3">Pourquoi CONFILEX</p>
            <h2 className="font-serif italic text-3xl sm:text-4xl text-[#0B3A2A]">
              Une infrastructure, pas une liste publique.
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {pillars.map((p) => (
              <div key={p.title} className="p-6 rounded-2xl border border-slate-100 bg-[#FAF7F1] hover:border-[#C9A961]/30 transition">
                <div className="w-10 h-10 rounded-full bg-[#0B3A2A] flex items-center justify-center mb-4">
                  <p.icon className="w-4 h-4 text-[#C9A961]" />
                </div>
                <h3 className="font-medium text-[#0B3A2A] mb-2">{p.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{p.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* OFFRES */}
      <section id="offres" className="py-20 bg-[#FAF7F1]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-14">
            <p className="text-[11px] uppercase tracking-[0.28em] text-[#C9A961] mb-3">Conditions d'affiliation</p>
            <h2 className="font-serif italic text-3xl sm:text-4xl text-[#0B3A2A] mb-4">
              Quatre leviers, à activer librement.
            </h2>
            <p className="text-sm text-slate-600 max-w-2xl mx-auto">
              Tous les montants ci-dessous sont indicatifs et modifiables. Aucune contrepartie financière
              n'est imposée à un professionnel sans son accord explicite.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            <OfferCard
              icon={Building2}
              label="Référencement standard"
              price={pricing.monthly_subscription.amount_eur}
              suffix="€ HT / mois"
              description={pricing.monthly_subscription.description}
              features={[
                "Profil référencé public",
                "Réception de dossiers qualifiés",
                "Messagerie & rendez-vous sécurisés",
                "Paiement intégré Stripe",
              ]}
              accent="navy"
            />
            <OfferCard
              icon={Sparkles}
              label="Mise en relation"
              price={pricing.lead_fee.amount_eur}
              suffix="€ HT / dossier"
              description={pricing.lead_fee.description}
              features={[
                "Paiement uniquement à la transmission",
                "Synthèse IA du dossier incluse",
                "Documents pré-organisés",
                "Acceptation libre par le spécialiste",
              ]}
              accent="gold"
            />
            <OfferCard
              icon={TrendingUp}
              label="Commission technologique"
              price={pricing.tech_commission_pct.amount_pct}
              suffix="%"
              description={pricing.tech_commission_pct.description}
              features={[
                "Appliquée sur consultations payées via CONFILEX",
                "Strictement encadrée par les règles déontologiques",
                "Transparente sur chaque transaction",
                "Désactivable à la demande",
              ]}
              accent="emerald"
            />
            <OfferCard
              icon={ShieldCheck}
              label="Visibilité premium"
              price={pricing.premium_visibility.amount_eur}
              suffix="€ HT / mois"
              description={pricing.premium_visibility.description}
              features={[
                "Mise en avant priorisée",
                "Fiche éditoriale enrichie",
                "Mise en avant Insights & publications",
                "Citation dans les contenus institutionnels",
              ]}
              accent="navy"
              featured
            />
          </div>

          <p className="text-xs text-slate-500 mt-10 max-w-3xl mx-auto text-center italic leading-relaxed">
            CONFILEX agit comme plateforme technologique d'intermédiation. Aucun lien de subordination
            n'est créé entre la plateforme et les professionnels référencés. Les commissions ne sont
            appliquées que dans le strict respect des règles déontologiques de chaque profession.
          </p>
        </div>
      </section>

      {/* TÉMOIGNAGE / CITATION */}
      <section className="py-20 bg-[#0B3A2A] text-white relative overflow-hidden">
        <ArtBackdrop src="/art/sorola/promenade-2.jpg" opacity={0.14} blur={30} saturate={0.6}
          overlay="linear-gradient(180deg, rgba(11,58,42,0.85) 0%, rgba(11,58,42,0.95) 100%)" />
        <div className="relative max-w-3xl mx-auto px-6 text-center">
          <p className="text-[11px] uppercase tracking-[0.3em] text-[#C9A961] mb-4">Esprit CONFILEX</p>
          <p className="font-serif italic text-2xl sm:text-3xl leading-relaxed text-white/95">
            « Notre engagement : ne référencer que des professionnels<br className="hidden sm:block" />
            que nous serions fiers de consulter nous-mêmes. »
          </p>
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="font-serif italic text-3xl sm:text-4xl text-[#0B3A2A] mb-5">
            Prêt à rejoindre ?
          </h2>
          <p className="text-slate-600 mb-8 leading-relaxed">
            Candidatez en quelques minutes. Notre équipe revient vers vous sous 48 à 72 heures ouvrées
            pour finaliser votre référencement.
          </p>
          <div className="flex gap-3 justify-center flex-wrap">
            <Link to="/expert/apply" data-testid="cta-apply-bottom"
              className="inline-flex items-center gap-2 bg-[#0B3A2A] text-white px-6 py-3 rounded-lg hover:brightness-110 transition">
              Candidater au référencement <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/about"
              className="inline-flex items-center gap-2 border border-[#0B3A2A]/20 text-[#0B3A2A] px-6 py-3 rounded-lg hover:bg-[#FAF7F1] transition">
              En savoir plus
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function OfferCard({ icon: Icon, label, price, suffix, description, features, accent, featured }) {
  const accentBg =
    accent === "gold" ? "bg-[#C9A961]/10 text-[#C9A961]" :
    accent === "emerald" ? "bg-emerald-100 text-emerald-700" :
    "bg-[#0B3A2A]/8 text-[#0B3A2A]";
  return (
    <div className={`rounded-2xl border p-6 flex flex-col ${
      featured
        ? "border-[#C9A961] bg-white shadow-xl shadow-[#C9A961]/10"
        : "border-slate-100 bg-white"
    }`}>
      <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-4 ${accentBg}`}>
        <Icon className="w-4 h-4" />
      </div>
      <p className="text-[11px] uppercase tracking-[0.18em] text-slate-500 mb-2">{label}</p>
      <div className="flex items-end gap-1 mb-3">
        <span className="font-serif text-4xl text-[#0B3A2A] tabular-nums">{price}</span>
        <span className="text-sm text-slate-500 mb-1.5">{suffix}</span>
      </div>
      <p className="text-xs text-slate-600 leading-relaxed mb-4">{description}</p>
      <ul className="space-y-1.5 mt-auto">
        {features.map((f) => (
          <li key={f} className="text-xs text-slate-600 flex items-start gap-1.5">
            <Check className="w-3.5 h-3.5 text-[#C9A961] mt-0.5 shrink-0" />
            <span>{f}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
